import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetMaterialityAssessment,
  getMandatoryStandardData,
  useAddMandatoryMaterialityAssessment,
} from './MaterialityAssessment.hooks';
import { ContentHeader, ContentLayout, Table } from 'Molecules';
import { Typography } from 'Tokens';
import { ColumnDef } from '@tanstack/react-table';
import { MaterialMap, MaterialStandard } from './MaterialityAssessment.d';
import { TableContainer, useDisclosure, VStack } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { ArrowNarrowRightIcon } from 'Tokens/Icons/Direction';
import { useCurrentCompany, useCurrentCompanyId } from 'utils/hooks';
import { MaterialityAssessmentHeader } from './MaterialitySettingsHeader';
import { Tag } from 'Atoms';
import { ORDER_OF_CATEGORIES } from 'utils/categoriesOrder';
import { useGetSubsidiaryType } from 'containers/Esrs/EsrsAssessment.hooks';
import {
  CollectOnlyMaterialityAssessment,
  CollectOnlyMaterialityStatus,
} from './CollectOnlyMaterialityAssessment';
import { MaterialityStatusBox } from './DoubleMaterialityAssessment/MaterialityUtils';
import { FrameworkRefs } from '../Assessment';
import {
  useGetCategoryAndStandardsQuery,
  useGetParentMaterialityAssessmentQuery,
  useGetSelectedDataPointsQuery,
} from 'models';
import { DatapointBrowserBox } from '../Assessment/DatapointBrowser/DatapointBrowserBox/DatapointBrowserBox';
import { DataPointsBank } from './DataPointsBank/DataPointsBank';
import { useFeatureFlags } from 'containers/Navigation';
import { useKnowledgeBase } from 'Features';

export const MaterialityAssessmentTable = ({
  framework,
  assessment,
  hasParentMaterialityAssessment,
  companyId,
  esrsAssessmentId,
  setStep,
  isOnboarding,
  isCollectOnly = false,
}: {
  framework: FrameworkRefs;
  assessment: MaterialMap;
  hasParentMaterialityAssessment: boolean;
  companyId: string | null;
  esrsAssessmentId: string;
  setStep?: (step: number) => void;
  isOnboarding: boolean;
  isCollectOnly?: boolean | null;
}) => {
  const navigate = useNavigate();
  const columns: ColumnDef<MaterialStandard>[] = useMemo(() => {
    const initial: ColumnDef<MaterialStandard>[] = [
      {
        header: 'Topic',
        accessorKey: 'standardName',
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <VStack spacing={0} alignItems="start">
              <Typography variant="bodyStrong">{row.original.standardName}</Typography>
              {framework !== FrameworkRefs.vsme && (
                <Typography variant="micro">{row.original.standardRef}</Typography>
              )}
            </VStack>
          );
        },
        meta: {
          width: '50%',
        },
      },
    ];

    if (hasParentMaterialityAssessment) {
      initial.push({
        header: 'Group materiality',
        accessorKey: 'Group materiality',
        enableSorting: false,
        cell: ({ row }) => {
          if (
            row.original.isParentMaterial === undefined ||
            row.original.isParentMaterial === null
          ) {
            return <Typography variant="body">Not assessed</Typography>;
          }
          if (!row.original.isTopical) return <Typography variant="body">Mandatory</Typography>;
          return row.original.isPhasedIn ? (
            <Typography variant="body">Phased-in</Typography>
          ) : row.original.isParentDataGathering ? (
            <Typography variant="body">
              {framework === FrameworkRefs.vsme ? 'Only collect' : 'Collect data'}
            </Typography>
          ) : row.original.isParentMaterial ? (
            <Typography variant="body">
              {framework === FrameworkRefs.vsme ? 'Collect and report' : 'Material'}
            </Typography>
          ) : (
            <Typography variant="body">
              {framework === FrameworkRefs.vsme ? 'Not collected' : 'Not material'}
            </Typography>
          );
        },
      });
    }
    initial.push({
      header: hasParentMaterialityAssessment ? 'Your company' : 'Status',
      accessorKey: hasParentMaterialityAssessment ? 'Your company' : 'Status',
      enableSorting: false,
      cell: ({ row }) => {
        if (isCollectOnly) {
          return <CollectOnlyMaterialityStatus row={row.original} />;
        }
        if (!row.original.isTopical) return <MaterialityStatusBox materiality="mandatory" />;
        if (row.original.isPhasedIn) {
          return <Tag title="Phased-in" variant="info" borderRadius="6px" />;
        }
        if (row.original.isDataGatheringOnly) {
          return (
            <Tag
              title={framework === FrameworkRefs.vsme ? 'Only collect' : 'Collect data'}
              variant="default"
              borderRadius="6px"
            />
          );
        }
        if (row.original.isMaterial === undefined || row.original.isMaterial === null) {
          return <MaterialityStatusBox materiality="toAssess" />;
        } else {
          return row.original.isMaterial ? (
            <MaterialityStatusBox materiality="material" />
          ) : (
            <MaterialityStatusBox materiality="notMaterial" />
          );
        }
      },
    });
    return initial;
  }, [hasParentMaterialityAssessment, isCollectOnly]);
  return (
    <TableContainer border="1px solid" borderRadius="8px" borderColor="border.decorative" w="100%">
      <Typography pt="12px" pl="16px" variant="h3">
        {assessment.categoryName}
      </Typography>
      <Table
        columns={columns}
        data={assessment.materialStandards}
        onRowClick={(row) => {
          if (isOnboarding) {
            navigate(
              `/${companyId}/sustainability-reporting/${framework.toLowerCase()}/${esrsAssessmentId}/onboarding/materiality/standard/${row.standardRef}`
            );
          } else navigate(`materiality/standard/${row.standardRef}`);
          setStep?.(3);
        }}
        rowProps={{
          _hover: {
            cursor: 'pointer',
            bg: 'bg.hover',
          },
        }}
        cellPadding="0px 16px"
        headerPadding="12px 16px"
        bottomRowBorder={false}
        rowIcon={<ArrowNarrowRightIcon />}
      />
    </TableContainer>
  );
};

export const MaterialityAssessment = ({
  setStep,
  isOnboarding = false,
}: {
  setStep?: (step: number) => void;
  isOnboarding?: boolean;
}) => {
  const { esrsAssessmentId = '', framework = '' } = useParams();
  const { companyId } = useCurrentCompanyId();
  const addMaterialStandard = useAddMandatoryMaterialityAssessment();

  const { assessmentCategories, loading } = useGetMaterialityAssessment(
    framework as FrameworkRefs,
    esrsAssessmentId
  );

  const { company } = useCurrentCompany();

  const { companyFrameworks } = useFeatureFlags({ company });
  const { onOpen: onKnowledgeBaseOpen } = useKnowledgeBase();

  const hasOtherMetrics = useMemo(() => companyFrameworks.length >= 2, [companyFrameworks]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: allStandardsAndCategories, loading: loadingAllStandardsAndCategories } =
    useGetCategoryAndStandardsQuery({
      variables: {
        assessmentId: esrsAssessmentId,
        framework,
        frameworkRefs: companyFrameworks,
      },
      skip: !esrsAssessmentId,
    });

  const {
    data: selectedDataPointsFromOtherFrameWork,
    loading: selectedDataPointsFromOtherFrameWorkLoading,
  } = useGetSelectedDataPointsQuery({
    variables: {
      assessmentId: esrsAssessmentId,
      framework,
    },
    skip: !esrsAssessmentId || !framework,
  });

  const { data: parentData, loading: parentDataLoading } = useGetParentMaterialityAssessmentQuery({
    variables: {
      childAssessmentId: esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const allStandardsAndCategoriesData = useMemo(
    () => allStandardsAndCategories?.MFCategory,
    [allStandardsAndCategories]
  );

  const selectedDataPointsFromOtherFrameWorkData = useMemo(
    () => selectedDataPointsFromOtherFrameWork?.EsrsMetric,
    [selectedDataPointsFromOtherFrameWork]
  );

  const parentAssessmentData = useMemo(() => parentData?.EsrsAssessment_by_pk, [parentData]);

  const { isCollectOnly, subsidiaryTypeLoading } = useGetSubsidiaryType(esrsAssessmentId);

  const hasParentMaterialityAssessment = useMemo(() => {
    return assessmentCategories[0]?.hasParentMaterialityAssessment;
  }, [assessmentCategories]);

  const hasMissingMandatoryMetrics = useMemo(() => {
    // ESRS-2
    const metrics =
      assessmentCategories[0]?.materialStandards[0]?.disclosureRequirementGroups.flatMap((drg) =>
        drg.requirements.flatMap((req) => req.metrics)
      );

    const materialMetrics = assessmentCategories[0]?.materialStandards[0]?.metrics ?? [];

    return metrics?.some(
      (metric) => !materialMetrics.some((mm) => mm.metricRef === metric.reference)
    );
  }, [assessmentCategories]);

  useEffect(() => {
    if (!isCollectOnly && hasMissingMandatoryMetrics && !loading) {
      const mandatoryStandards = getMandatoryStandardData(assessmentCategories);
      addMaterialStandard(mandatoryStandards);
    }
  }, [isCollectOnly, loading, hasMissingMandatoryMetrics]);

  if (isCollectOnly)
    return (
      <CollectOnlyMaterialityAssessment
        materialityAssessments={assessmentCategories}
        hasParentMaterialityAssessment={hasParentMaterialityAssessment}
        companyId={companyId}
        loading={loading || subsidiaryTypeLoading}
        setStep={setStep}
        isOnboarding={isOnboarding}
      />
    );
  return (
    <ContentLayout
      noHeader
      variant="inline.nopad"
      isLoading={
        loading ||
        subsidiaryTypeLoading ||
        parentDataLoading ||
        selectedDataPointsFromOtherFrameWorkLoading ||
        loadingAllStandardsAndCategories
      }
      header={!setStep && isOnboarding && <ContentHeader title="Materiality assessment" />}
    >
      <VStack mt={isOnboarding ? '0px' : '28px'} w="100%" alignItems="stretch" spacing="24px">
        {!isOnboarding && (
          <MaterialityAssessmentHeader title="Select which disclosures to collect and report data on" />
        )}

        {/* sort the general one to always appear first */}
        {assessmentCategories
          .sort(
            (a, b) => ORDER_OF_CATEGORIES?.[a.categoryName] - ORDER_OF_CATEGORIES?.[b.categoryName]
          )
          .map((mAssessment) => {
            return (
              <MaterialityAssessmentTable
                framework={framework as FrameworkRefs}
                assessment={mAssessment}
                hasParentMaterialityAssessment={hasParentMaterialityAssessment}
                esrsAssessmentId={esrsAssessmentId}
                companyId={companyId}
                isOnboarding={isOnboarding}
                setStep={setStep}
              />
            );
          })}
        {!isOnboarding && hasOtherMetrics && (
          <VStack w="100%" alignItems="stretch" spacing="24px">
            <VStack w="100%" gap={'16px'}>
              <VStack
                gap={'2px'}
                alignItems={'start'}
                borderBottom={'1px solid  rgba(15, 15, 46, 0.12)'}
                paddingBottom={'8px'}
                w="100%"
              >
                <Typography variant="h2">Other frameworks (optional)</Typography>
                <Typography
                  variant="body"
                  color="text.muted"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                >
                  Optional step. You can browse and add entity specific data points to add to your
                  report
                </Typography>
              </VStack>
            </VStack>
            <DatapointBrowserBox
              onClickBrowse={() => onOpen()}
              onClickLearnMore={() => onKnowledgeBaseOpen('mf-data-points-database')}
            />
            <DataPointsBank
              onClose={onClose}
              isOpen={isOpen}
              allStandardsAndCategoriesData={allStandardsAndCategoriesData}
              selectedDataPointsFromOtherFrameWork={selectedDataPointsFromOtherFrameWorkData}
              parentAssessmentData={parentAssessmentData}
              isCollectOnly={isCollectOnly}
            />
          </VStack>
        )}
      </VStack>
    </ContentLayout>
  );
};
